import styled, { css, keyframes } from "styled-components";

import { colors, fonts, fontSizes, radius, spacing, transitions } from "@/styles/design-system";

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  15% { transform: translateX(-4px); }
  30% { transform: translateX(4px); }
  45% { transform: translateX(-3px); }
  60% { transform: translateX(3px); }
  75% { transform: translateX(-1px); }
  90% { transform: translateX(1px); }
`;

/* ── Overlay ── */

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* ── Modal Container ── */

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to   { opacity: 0; transform: translateY(24px) scale(0.97); }
`;

export const ModalContainer = styled.div<{ $closing?: boolean }>`
  position: relative;
  width: 92%;
  max-width: 560px;
  /* Static glass snapshot (2026-05-13) — see CartModal/styles.ts for context. */
  background:
    radial-gradient(ellipse 45% 40% at 42% -3%, rgba(176, 144, 105, 0.9) 0%, rgba(160, 128, 90, 0.5) 35%, transparent 75%),
    radial-gradient(ellipse 55% 50% at 58% 3%, rgba(170, 140, 100, 0.75) 0%, rgba(140, 110, 78, 0.35) 45%, transparent 85%),
    radial-gradient(ellipse 70% 35% at 50% 8%, rgba(150, 118, 82, 0.4) 0%, transparent 70%),
    linear-gradient(180deg, rgb(153, 112, 75) 0%, rgb(99, 74, 54) 50%, rgb(111, 83, 60) 100%);
  border: none;
  border-radius: ${radius["4xl"]};
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7), 0 20px 60px rgba(0, 0, 0, 0.6);
  max-height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${({ $closing }) => ($closing ? slideDown : slideUp)} 0.25s ease both;
`;

/* ── Close Button ── */

export const ModalHead = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 16px 8px;
  flex-shrink: 0;
`;

export const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(235, 194, 142, 0.2);
  border: 1px solid rgba(235, 194, 142, 0.3);
  /* Dim by default → fully white on hover. */
  color: rgba(255, 255, 255, 0.55);
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(235, 194, 142, 0.35);
    border-color: rgba(235, 194, 142, 0.5);
    color: #ffffff;
  }
`;

/* ── Step Indicator ── */

export const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.md};
  padding: 24px 48px 8px;
`;

export const StepDot = styled.div<{ $state: "default" | "active" | "done" }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.number};
  font-size: ${fontSizes.sm};
  font-weight: 600;
  transition: all ${transitions.slow};

  ${({ $state }) => {
    switch ($state) {
      case "active":
        return `
          border: 1.5px solid ${colors.gold.light};
          color: ${colors.brown.darkest};
          background: linear-gradient(135deg, ${colors.gold.light} 0%, #ffebb5 100%);
        `;
      case "done":
        return `
          border: 1.5px solid ${colors.accent.green};
          color: ${colors.accent.green};
          background: rgba(52, 211, 153, 0.12);
        `;
      default:
        return `
          border: 1.5px solid rgba(235, 194, 142, 0.25);
          color: ${colors.text.muted};
          background: transparent;
        `;
    }
  }}
`;

export const StepLine = styled.div<{ $done?: boolean }>`
  width: 40px;
  height: 1.5px;
  background: ${({ $done }) => ($done ? colors.accent.green : "rgba(235, 194, 142, 0.2)")};
  transition: background ${transitions.slow};
`;

/* ── Slider ── */

export const SliderViewport = styled.div`
  overflow-x: hidden;
  overflow-y: hidden;
  scrollbar-gutter: stable;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 12px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.5);
    border-radius: 6px;
    border: 4px solid transparent;
    background-clip: padding-box;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(235, 194, 142, 0.7);
    background-clip: padding-box;
  }
`;

export const SliderTrack = styled.div<{ $step: number }>`
  display: flex;
  align-items: flex-start;
  width: 200%;
  transform: translateX(${({ $step }) => -$step * 50}%);
  transition: transform 0.4s ease;
`;

export const SlidePanel = styled.div`
  width: 50%;
  flex-shrink: 0;
  padding: 0 36px 48px 48px;

  /* phone: trim the wide desktop side padding so the numbered steps and
     credit/warning boxes get usable width */
  @media (max-width: 600px) {
    padding: 0 16px 32px 16px;
  }
`;

/* ── Step 1: Track Info ── */

export const TrackInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xl};
  width: 100%;
`;

export const TrackCover = styled.img`
  width: 180px;
  height: 180px;
  border-radius: ${radius["2xl"]};
  object-fit: cover;
  border: 1px solid ${colors.border.light};
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.4),
    0 0 60px rgba(186, 144, 92, 0.08);
`;

export const TrackCoverPlaceholder = styled.div`
  width: 180px;
  height: 180px;
  border-radius: ${radius["2xl"]};
  background: linear-gradient(145deg, ${colors.brown.primary} 0%, ${colors.brown.darkest} 100%);
  border: 1px solid ${colors.border.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(235, 194, 142, 0.25);
  box-shadow:
    0 12px 40px rgba(0, 0, 0, 0.4),
    0 0 60px rgba(186, 144, 92, 0.08);
`;

export const TrackMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TrackName = styled.div`
  font-family: ${fonts.heading};
  font-size: ${fontSizes["2xl"]};
  font-weight: 500;
  color: ${colors.text.heading};
  letter-spacing: 0.4px;
  line-height: 1.2;
`;

export const TrackArtist = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.subtle};
  letter-spacing: 0.5px;
`;

export const TierBadge = styled.span<{ $variant: "free" | "premium" }>`
  display: inline-block;
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: 600;
  padding: 2px 8px;
  border-radius: ${radius.md};
  letter-spacing: 0.3px;

  ${({ $variant }) => {
    switch ($variant) {
      case "free":
        return `color: ${colors.accent.green}; background: rgba(52, 211, 153, 0.15);`;
      case "premium":
        return `color: ${colors.gold.text}; background: rgba(186, 144, 92, 0.2);`;
    }
  }}
`;

/* ── Format Buttons ── */

export const FormatDivider = styled.div`
  width: 100%;
  height: 1px;
  background: ${colors.border.light};
`;

export const FormatLabel = styled.div`
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  font-weight: 500;
  color: ${colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 14px;
  text-align: center;
`;

export const FormatButtons = styled.div`
  display: flex;
  gap: 12px;
`;

export const FormatBtn = styled.button<{ $locked?: boolean; $shaking?: boolean }>`
  flex: 1;
  padding: 20px 14px;
  border-radius: ${radius.xl};
  border: 1px solid ${({ $locked }) => ($locked ? colors.border.light : "rgba(156, 211, 253, 0.3)")};
  background: ${({ $locked }) => ($locked ? colors.surface.glass : "rgba(156, 211, 253, 0.06)")};
  cursor: pointer;
  transition: all ${transitions.slow};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:hover {
    border-color: rgba(156, 211, 253, 0.5);
    background: rgba(156, 211, 253, 0.1);
    box-shadow: 0 0 24px rgba(156, 211, 253, 0.1);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  ${({ $shaking }) =>
    $shaking &&
    css`
      animation: ${shake} 0.5s ease;
      border-color: ${colors.gold.primary} !important;
    `}
`;

export const FormatName = styled.div`
  font-family: ${fonts.number};
  font-size: ${fontSizes.lg};
  font-weight: 700;
  color: ${colors.text.heading};
  margin-bottom: 2px;
  letter-spacing: 0.3px;
`;

export const FormatSize = styled.div<{ $highlight?: boolean }>`
  font-family: ${fonts.number};
  font-size: ${fontSizes.sm};
  color: ${({ $highlight }) => ($highlight ? colors.gold.light : colors.text.muted)};
  font-weight: ${({ $highlight }) => ($highlight ? 600 : 400)};
  transition: color 0.3s ease;
`;

/* ── Step 2: Next Steps ── */

export const NextStepsTitle = styled.h3`
  font-family: ${fonts.heading};
  font-size: ${fontSizes["2xl"]};
  font-weight: 500;
  color: ${colors.text.heading};
  text-align: center;
  letter-spacing: 0.4px;
  margin: 0 0 ${spacing.lg} 0;
`;

/* -- Credit Block -- */

export const CreditBlock = styled.div`
  margin-top: ${spacing.lg};
`;

export const CreditTextBox = styled.div`
  font-family: "SF Mono", "Fira Code", "Fira Mono", "Roboto Mono", "Consolas", "Monaco", "Menlo", monospace;
  font-size: ${fontSizes.body};
  color: ${colors.text.muted};
  line-height: 1.2;
  letter-spacing: -0.5px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: ${radius.md};
  padding: ${spacing.lg};
  margin-bottom: 14px;
  white-space: pre-line;
  word-break: break-word;
  max-height: 72px;
  overflow-y: scroll; /* always show the scrollbar as a scroll affordance */
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(60, 45, 35, 0.5);
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(235, 194, 142, 0.4);
    border-radius: 4px;
  }

  /* phone: smaller monospace + relaxed tracking so the credit lines fit the
     narrower box without horizontal cramping; a touch more height. iOS Safari
     never persists the native scrollbar, so hide it here and let the wrapping
     CreditScrollWrap draw an always-visible custom one. */
  @media (max-width: 600px) {
    font-size: 0.75rem;
    letter-spacing: 0;
    padding: 12px 18px 12px 12px;
    max-height: 96px;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      width: 0;
      display: none;
    }
  }
`;

/* Credit box + always-visible custom scrollbar (phone). The native iOS
   scrollbar only flashes during scroll; this overlay track/thumb stays put so
   the box always reads as scrollable. */
export const CreditScrollWrap = styled.div`
  position: relative;
`;

export const CreditScrollbar = styled.div`
  display: none;

  @media (max-width: 600px) {
    display: block;
    position: absolute;
    top: 10px;
    right: 6px;
    bottom: 10px;
    width: 4px;
    border-radius: 2px;
    background: rgba(60, 45, 35, 0.6);
    pointer-events: none;

    /* thumb */
    &::after {
      content: "";
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      height: 45%;
      border-radius: 2px;
      background: rgba(235, 194, 142, 0.55);
    }
  }
`;

export const CopyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: ${radius.md};
  border: 1px solid rgba(156, 211, 253, 0.3);
  background: rgba(156, 211, 253, 0.08);
  color: ${colors.accent.blue};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: all ${transitions.slow};

  &:hover {
    background: rgba(156, 211, 253, 0.15);
    border-color: rgba(156, 211, 253, 0.5);
    box-shadow: 0 0 16px rgba(156, 211, 253, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const InlineCopyBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: ${radius.sm};
  border: 1px solid rgba(156, 211, 253, 0.25);
  background: rgba(156, 211, 253, 0.08);
  color: ${colors.accent.blue};
  cursor: pointer;
  transition: all ${transitions.normal};

  &:hover {
    background: rgba(156, 211, 253, 0.2);
    border-color: rgba(156, 211, 253, 0.5);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ClaimsWarningBox = styled.div`
  margin-top: 14px;
  padding: 12px 16px;
  border-radius: ${radius.md};
  background: rgba(228, 122, 106, 0.08);
  border: 1px solid rgba(228, 122, 106, 0.25);
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
  line-height: 1.5;

  strong {
    color: ${colors.accent.red};
    font-weight: 600;
  }
`;

export const TimestampOr = styled.div`
  text-align: center;
  font-family: ${fonts.body};
  font-size: ${fontSizes.sm};
  color: ${colors.text.muted};
  padding: 4px 0;
`;

/* -- Steps List -- */

export const StepsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 28px;

  /* Vertical line from below first circle to the curve */
  &::before {
    content: "";
    position: absolute;
    left: 15px; /* center of 32px step num circle */
    top: 32px; /* start below first circle */
    bottom: 43px; /* stop where curve's top edge begins */
    width: 1px;
    background: ${colors.border.light};
  }

  /* Curved corner: vertical to horizontal */
  &::after {
    content: "";
    position: absolute;
    left: 15px;
    bottom: 23px;
    width: 28px;
    height: 20px;
    border-left: 1px solid ${colors.border.light};
    border-bottom: 1px solid ${colors.border.light};
    border-bottom-left-radius: 12px;
    border-right: none;
    border-top: none;
  }
`;

export const StepsList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StepItem = styled.div`
  display: flex;
  gap: 16px;
`;

export const StepNumCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

export const StepNum = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${fonts.number};
  font-size: ${fontSizes.sm};
  font-weight: 700;
  color: ${colors.brown.darkest};
  flex-shrink: 0;
  background: linear-gradient(135deg, ${colors.gold.light} 0%, #ffebb5 100%);
  position: relative;
  z-index: 1;
  padding-right: 1px;
  /* nudge the digit ~1px lower so it reads optically centred in the circle */
  padding-top: 1px;
  box-shadow: 0 0 12px rgba(186, 144, 92, 0.2);
`;

export const StepConnector = styled.div`
  width: 1.5px;
  flex: 1;
  background: ${colors.border.light};
  min-height: 12px;
`;

export const StepContent = styled.div<{ $last?: boolean }>`
  padding-bottom: ${({ $last }) => ($last ? "0" : "40px")};
`;

export const StepLabel = styled.div`
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: 700;
  color: ${colors.gold.light};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 2px;
`;

export const StepContentTitle = styled.div`
  font-size: ${fontSizes.lg};
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: ${spacing.xs};
  /* tight line spacing so a wrapped title doesn't gap apart; padding-top
     re-centres the first line against the 32px step-number circle */
  line-height: 1.3;
  padding-top: 4px;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;

  /* phone: slightly smaller so titles wrap less */
  @media (max-width: 600px) {
    font-size: 1rem;
    padding-top: 6px;
  }
`;

export const StepContentDesc = styled.div`
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
  /* tighter line spacing so multi-line descriptions stay compact (all sizes) */
  line-height: 1.3;
`;

/* -- Channel Card -- */

export const ChannelCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  background: ${colors.surface.glass};
  border: 1px solid ${colors.border.light};
  border-radius: ${radius.xl};
  padding: 14px ${spacing.xl};
  transition: border-color ${transitions.normal};

  &:hover {
    border-color: ${colors.border.default};
  }
`;

export const ChannelAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${colors.gold.gradient};
  flex-shrink: 0;
`;

export const ChannelName = styled.div`
  font-size: ${fontSizes.body};
  font-weight: 600;
  color: ${colors.text.primary};
  letter-spacing: 0.2px;
`;

export const ChannelStatus = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${fontSizes.sm};
  font-weight: 600;
  color: ${colors.accent.green};

  svg {
    width: 14px;
    height: 14px;
  }
`;

/* -- Connect Prompt -- */

export const ConnectPrompt = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  background: rgba(156, 211, 253, 0.06);
  border: 1px solid rgba(156, 211, 253, 0.2);
  border-radius: ${radius.xl};
  padding: ${spacing.xl} ${spacing["2xl"]};
`;

export const ConnectPromptIcon = styled.div`
  flex-shrink: 0;
  color: ${colors.accent.blue};
`;

export const ConnectPromptText = styled.div`
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
  line-height: 1.5;

  a {
    color: ${colors.accent.blue};
    text-decoration: none;
    font-weight: 500;
    &:hover {
      color: #c4e4ff;
    }
  }
`;

/* -- Result Hint -- */

export const ResultHint = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(52, 211, 153, 0.06);
  border: 1px solid rgba(52, 211, 153, 0.15);
  border-radius: ${radius.lg};
  padding: 14px ${spacing.xl};
`;

export const ResultHintIcon = styled.div`
  flex-shrink: 0;
  color: ${colors.accent.green};
`;

export const ResultHintText = styled.div`
  font-size: ${fontSizes.body};
  color: ${colors.text.body};
  line-height: 1.5;
`;

/* -- Primary Button -- */

export const PrimaryButton = styled.button`
  display: block;
  margin-left: 44px; /* gap after curve end */
  width: calc(100% - 44px);
  padding: 16px 28px;
  background: linear-gradient(135deg, ${colors.gold.light} 0%, #ffebb5 100%);
  color: ${colors.brown.darkest};
  font-family: ${fonts.body};
  font-size: ${fontSizes.body};
  font-weight: 700;
  border: none;
  border-radius: ${radius.xl};
  cursor: pointer;
  box-shadow: 0 0 32px rgba(186, 144, 92, 0.3);
  transition: all ${transitions.slow};
  letter-spacing: 0.3px;
  text-align: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 40px rgba(186, 144, 92, 0.6);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

/* ── Dev connected/not-connected toggle ── */

export const DevConnectedToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 0 0;
`;

export const DevBadge = styled.span`
  font-family: ${fonts.number};
  font-size: ${fontSizes.xs};
  font-weight: 600;
  color: ${colors.accent.red};
  background: rgba(228, 122, 106, 0.15);
  border: 1px solid rgba(228, 122, 106, 0.3);
  border-radius: ${radius.sm};
  padding: 2px 8px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

export const DevTabContainer = styled.div`
  display: flex;
  gap: 2px;
  padding: 2px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(186, 144, 92, 0.2);
  border-radius: 6px;
`;

export const DevTab = styled.button<{ $active: boolean }>`
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  font-family: ${fonts.body};
  font-size: ${fontSizes.xs};
  font-weight: 400;
  color: ${({ $active }) => ($active ? colors.text.primary : "rgba(251, 244, 236, 0.65)")};
  background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "transparent")};
  border: none;
  cursor: pointer;
  transition: all ${transitions.normal};
  white-space: nowrap;

  &:hover {
    color: ${colors.text.primary};
    background: ${({ $active }) => ($active ? "rgba(255, 220, 168, 0.15)" : "rgba(255, 255, 255, 0.06)")};
  }
`;
